import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppwriteApiEndpoint, AppwriteApiRoute, Collection, GlobalVariableDocumentIds, User, UserGroups, UserNotification } from '@prism/prism-shared';
import { environment } from 'apps/prism-client/src/environments/environment';
import { Query } from 'appwrite';
import { Observable, Subject, catchError, debounceTime, defaultIfEmpty, forkJoin, from, map, merge, of, switchMap, timer } from 'rxjs';
import { AppwriteDatabaseService } from '@prism/angular-shared';
import { AppwriteFunctionsService } from './appwrite-functions.service';

@Injectable({ providedIn: 'root' })
export class UserApiService {
    private userNotificationsRefresh$: Subject<void> = new Subject<void>();

    constructor(
        private http: HttpClient,
        private database: AppwriteDatabaseService,
        private functions: AppwriteFunctionsService,
    ) { }

    private userPath(endpoint: AppwriteApiEndpoint | string): string {
        return `/${AppwriteApiRoute.User}/${endpoint}`;
    }

    private appwriteFunctionBaseUrl(): string {
        return environment.appwrite.endpoint
            .replace(/\/v1\/?$/, '')
            .replace('://data.', '://api.');
    }

    public getUsers(): Observable<User[]> {
        return this.database.list<User>(Collection.user, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public resetPassword(user: User): Observable<{ message: string; tempPassword: string }> {
        return this.functions.post<{ message: string; tempPassword?: string; error?: unknown }>(
            'api',
            this.userPath(AppwriteApiEndpoint.ResetPassword),
            { userId: user.id },
        ).pipe(
            map((response) => {
                if (response.message !== 'Success' || !response.tempPassword) {
                    throw new Error('Password reset failed');
                }

                return {
                    message: response.message,
                    tempPassword: response.tempPassword,
                };
            }),
        );
    }

    public createAuthUser(user: User): Observable<any> {
        return this.functions.post('api', this.userPath(AppwriteApiEndpoint.CreateUser), user);
    }

    public createNonEmailAuthUser(user: User): Observable<any> {
        return this.functions.post('api', this.userPath(AppwriteApiEndpoint.CreateNonEmailUser), user);
    }

    public createAppwriteUser(user: User): Observable<void> {
        return from(this.database.collection<User>(Collection.user).doc(user.id).set(user));
    }

    public createUserDocument(user: User): Observable<void> {
        return from(this.database.collection<User>(Collection.user).doc(user.id).set(user));
    }

    public updateUser(user: User) {
        return forkJoin([
            this.functions.post('api', this.userPath(AppwriteApiEndpoint.UpdateUser), user),
            from(this.database.collection<User>(Collection.user).doc(user.id).set(user)),
        ]);
    }

    public updateNonEmailUser(user: User) {
        return forkJoin([
            this.functions.post('api', this.userPath(AppwriteApiEndpoint.UpdateNonEmailUser), user),
            from(this.database.collection<User>(Collection.user).doc(user.id).set(user)),
        ]);
    }

    public loadUserByBadgeId(badgeId: string): Observable<User | null> {
        return this.database.list<User>(Collection.user, [
            Query.equal('badgeId', badgeId),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0] ?? null),
        );
    }

    public setUser(user: User): Observable<void> {
        return from(this.database.collection<User>(Collection.user).doc(user.id).set(user));
    }

    public setUsers(users: User[]): Observable<void> {
        if (users.length === 0) {
            return of(void 0);
        }

        return forkJoin(users.map(user => this.setUser(user))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public getUserById(userId: string): Observable<User> {
        return this.database.get<User>(Collection.user, userId).pipe(
            catchError(() => of(null as unknown as User)),
        );
    }

    public setUserGroups(userGroups: UserGroups): Observable<void> {
        return from(this.database.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.UserGroups).set(userGroups));
    }

    public loadUserGroups(): Observable<UserGroups> {
        return this.database.get<UserGroups>(Collection.globalVariables, GlobalVariableDocumentIds.UserGroups);
    }

    public getUserValueChangesByUserId(userId: string): Observable<User> {
        return this.database.collection<User>(Collection.user).doc(userId).valueChanges() as Observable<User>;
    }

    public toggleUserActive(user: User, active: boolean) {
        return this.functions.post('api', this.userPath(AppwriteApiEndpoint.DeactivateUser), { active, user }).pipe(
            switchMap(() => from(this.database.collection<User>(Collection.user).doc(user.id).set({ ...user, active }))),
        );
    }

    public checkUserOnNetwork(): Observable<{ onNetwork: boolean }> {
        return this.http.get<{ onNetwork: boolean }>(`${this.appwriteFunctionBaseUrl()}/${AppwriteApiEndpoint.CheckOnNetwork}`);
    }

    public setUserNotification(userNotification: UserNotification): Observable<void> {
        return from(this.database.collection<UserNotification>(Collection.userNotification).doc(userNotification.id).set(userNotification));
    }

    public setUserNotifications(userNotifications: UserNotification[]): Observable<void> {
        if (userNotifications.length === 0) {
            return of(void 0);
        }

        return forkJoin(userNotifications.map(notification => this.setUserNotification(notification))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public getUserNotificationsValueChanges(userId: string): Observable<UserNotification[]> {
        const pollingIntervalMs = 60_000;

        return merge(
            timer(0, pollingIntervalMs),
            this.userNotificationsRefresh$.pipe(debounceTime(500)),
        ).pipe(
            switchMap(() => {
                const timeOffset = new Date();
                timeOffset.setDate(timeOffset.getDate() - 1);

                return forkJoin({
                    unacknowledged: this.database.list<UserNotification>(Collection.userNotification, [
                        Query.equal('userId', userId),
                        Query.isNull('acknowledgedOn'),
                        Query.orderDesc('createdOn'),
                        Query.limit(1000),
                    ]).pipe(
                        map(resp => resp.items),
                    ),
                    recent: this.database.list<UserNotification>(Collection.userNotification, [
                        Query.equal('userId', userId),
                        Query.greaterThanEqual('acknowledgedOn', timeOffset.toISOString()),
                        Query.orderDesc('acknowledgedOn'),
                        Query.limit(1000),
                    ]).pipe(
                        map(resp => resp.items),
                    ),
                }).pipe(
                    map(({ unacknowledged, recent }) => [...unacknowledged, ...recent]),
                    catchError(() => of([])),
                );
            }),
        );
    }

    public getUnacknowledgedUserNotificationCountValueChanges(userId: string): Observable<number> {
        const pollingIntervalMs = 60_000;

        return merge(
            timer(0, pollingIntervalMs),
            this.userNotificationsRefresh$.pipe(debounceTime(500)),
        ).pipe(
            switchMap(() => this.database.list<UserNotification>(Collection.userNotification, [
                Query.equal('userId', userId),
                Query.isNull('acknowledgedOn'),
                Query.orderDesc('createdOn'),
                Query.limit(1),
            ])),
            map(resp => resp.total),
            catchError(() => of(0)),
        );
    }

    public refreshUserNotifications(): void {
        this.userNotificationsRefresh$.next();
    }

    public setUserPassword(userId: string, password: string): Observable<any> {
        return this.functions.post('api', this.userPath(AppwriteApiEndpoint.SetUserPassword), { userId, password });
    }

    public loadUserNotifications(): Observable<UserNotification[]> {
        return this.database.list<UserNotification>(Collection.userNotification, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }
}
